import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [invitationCode, setInvitationCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    setLoading(true);
    try {
      const userData = await login(username, password);
      toast.success("Bine ati venit !");
      // Redirect based on role
      if (userData?.role === "photographer") {
        navigate("/photographer");
      } else if (userData?.role === "mechanic") {
        navigate("/mechanic-dashboard");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Échec de la connexion");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!username || !password || !invitationCode) {
      toast.error("Veuillez remplir tous les champs, y compris le code d'invitation");
      return;
    }
    if (password.length < 4) {
      toast.error("Le mot de passe doit contenir au moins 4 caractères");
      return;
    }
    setLoading(true);
    try {
      await register(username, password, invitationCode);
      toast.success("Compte créé avec succès !");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Échec de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{
          backgroundImage: "url('https://images.pexels.com/photos/3822784/pexels-photo-3822784.jpeg')"
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background to-background" />

      <Card className="w-full max-w-md relative z-10 border-border bg-card/95 backdrop-blur-sm">
        <CardHeader className="text-center pb-2">
          <img src="https://customer-assets.emergentagent.com/job_509254e3-4fbd-4a68-8194-b9dcfb6ff3e4/artifacts/bylh1er6_Image.jpg" alt="Scutersh" className="mx-auto w-16 h-16 rounded-lg object-contain mb-4" />
          <CardTitle className="font-heading font-black text-2xl tracking-tight">
            SCUTERSH
          </CardTitle>
          <CardDescription className="font-mono text-xs uppercase tracking-widest">
            Management • Internal Access
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="tab-login">Conectare</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Inregistrare</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-username">Utilizator</Label>
                  <Input
                    id="login-username"
                    type="text"
                    placeholder="Introduceti utilizatorul"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="font-mono"
                    data-testid="input-login-username"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Parola</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Introduceti parola"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="font-mono pr-10"
                      data-testid="input-login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-primary text-black font-bold uppercase tracking-wide hover:bg-primary/90"
                  disabled={loading}
                  data-testid="btn-login"
                >
                  {loading ? "Se conecteaza..." : "CONECTARE"}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="register-username">Nom d'utilisateur</Label>
                  <Input
                    id="register-username"
                    type="text"
                    placeholder="Choisir un nom d'utilisateur"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="font-mono"
                    data-testid="input-register-username"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-password">Mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="register-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Créer un mot de passe"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="font-mono pr-10"
                      data-testid="input-register-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-invitation">Code d'invitation</Label>
                  <Input
                    id="register-invitation"
                    type="text"
                    placeholder="Entrer le code d'invitation"
                    value={invitationCode}
                    onChange={(e) => setInvitationCode(e.target.value)}
                    className="font-mono uppercase"
                    data-testid="input-register-invitation"
                  />
                  <p className="text-xs text-muted-foreground">
                    Demandez le code d'invitation à l'administrateur
                  </p>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-primary text-black font-bold uppercase tracking-wide hover:bg-primary/90"
                  disabled={loading}
                  data-testid="btn-register"
                >
                  {loading ? "Création du compte..." : "Créer un compte"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
